import React, { Fragment, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import SEO from "../../components/seo";
import LayoutOne from "../../layouts/LayoutOne";
import NewsletterBar from "../../components/newdesign/NewsletterBar";
import { getDiscountPrice } from "../../helpers/product";
import { formatMoney } from "../../components/cart-redesign/pricing";
import LookupPanel from "../../components/return-redesign/LookupPanel";
import StateMessage from "../../components/return-redesign/StateMessage";
import SummaryBar from "../../components/return-redesign/SummaryBar";
import StepCard from "../../components/return-redesign/StepCard";
import ItemPicker from "../../components/return-redesign/ItemPicker";
import ChoiceGrid from "../../components/return-redesign/ChoiceGrid";
import PhotoDropzone from "../../components/return-redesign/PhotoDropzone";
import PolicyNote from "../../components/return-redesign/PolicyNote";
import {
  RETURN_REASONS,
  RESOLUTIONS,
  composeReason,
} from "../../components/return-redesign/refundFlow";

/* Order statuses read back in the customer's language, not the stored value. */
const ORDER_STATUS_LABELS = {
  confirmed: "Confirmed",
  processing: "Processing",
  Dispatch: "Dispatched",
  Delivered: "Delivered",
};

function Refund() {
  const [order, setOrder] = useState(null);
  const [lookupError, setLookupError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState("");
  const [returnQty, setReturnQty] = useState(1);
  const [reasonChoice, setReasonChoice] = useState("");
  const [resolution, setResolution] = useState("");
  const [comments, setComments] = useState("");
  const [images, setImages] = useState([]);

  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const currency = useSelector((state) => state.currency);
  const { products } = useSelector((state) => state.product);

  const baseURL = process.env.REACT_APP_BACKEND_URL;

  // 🔹 Fetch order by ID
  const fetchOrder = async (orderId) => {
    if (!orderId) return;

    setLoading(true);
    setSearched(true);
    setLookupError("");
    setSubmitted(null);
    setFormError("");
    try {
      const response = await axios.get(
        `${baseURL}/refund/getrefundorders/${orderId}`
      );
      setOrder(response.data);
      setSelectedProduct("");
      setReturnQty(1);
    } catch (error) {
      console.error("Error fetching order:", error);
      setOrder(null);
      setLookupError(
        error?.response?.data?.message ||
          "We couldn't find that order. Returns open once an order leaves the pending stage."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * The refund endpoint populates only each item's `name`, so the image and
   * unit price are looked up in the catalogue already held in the store —
   * the same approach the order pages take.
   */
  const lines = useMemo(() => {
    if (!order?.items) return [];
    const rate = currency.currencyRate || 1;

    return order.items.map((item) => {
      const productId = item.product?._id || item.product;
      const full = products.find((candidate) => candidate._id === productId);
      const base = full?.price?.basePrice;
      const discounted =
        base != null ? getDiscountPrice(base, full.discount) : null;
      const unit =
        base == null ? null : +((discounted ?? base) * rate).toFixed(2);

      return {
        id: String(productId || item._id),
        name: item.product?.name || full?.name || "Product",
        quantity: item.quantity,
        image: full?.mainImage,
        lineTotal: unit == null ? null : +(unit * item.quantity).toFixed(2),
      };
    });
  }, [order, products, currency.currencyRate]);

  const formatPrice = (value) => formatMoney(currency, value);

  const selectedLine = lines.find((line) => line.id === selectedProduct);

  const orderNumber =
    order?.customFullOrderId ||
    (order?.customOrderId ? `#${order.customOrderId}` : order?._id);

  const handleSelect = (id) => {
    setSelectedProduct(id);
    setReturnQty(1);
  };

  // 🔹 Submit refund request
  const handleRefundSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProduct) {
      setFormError("Choose the item you'd like to return.");
      return;
    }
    if (!reasonChoice) {
      setFormError("Pick a reason so we know how to handle the return.");
      return;
    }

    setFormError("");
    setSubmitting(true);

    /*
     * The API stores one free-text `reason`; the categorised answer, the
     * quantity and the preferred resolution are composed into it so no new
     * fields are needed on the request.
     */
    const formData = new FormData();
    formData.append("orderId", order._id);
    formData.append("ProductId", selectedProduct);
    formData.append(
      "reason",
      composeReason({
        reason: reasonChoice,
        quantity: returnQty,
        ordered: selectedLine?.quantity || 1,
        resolution,
        comments,
      })
    );
    images.forEach((file) => formData.append("additionalImages", file));

    try {
      const response = await axios.post(
        `${baseURL}/refund/requestrefund`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSubmitted(response.data);
      setSelectedProduct("");
      setReturnQty(1);
      setReasonChoice("");
      setResolution("");
      setComments("");
      setImages([]);
    } catch (error) {
      console.error("Error submitting refund:", error);
      setFormError(
        error?.response?.data?.message ||
          "We couldn't submit that request. Please try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const results = () => {
    if (loading) {
      return (
        <StateMessage
          tone="loading"
          title="Looking up your order…"
          text="This only takes a moment."
        />
      );
    }

    if (lookupError) {
      return (
        <StateMessage tone="error" title="We couldn't open a return" text={lookupError}>
          <div className="c4rtn__actions">
            <Link
              className="c4rtn__btn c4rtn__btn--outline"
              to={process.env.PUBLIC_URL + "/order-status"}
            >
              Track Your Order
            </Link>
            <Link
              className="c4rtn__btn c4rtn__btn--outline"
              to={process.env.PUBLIC_URL + "/contact"}
            >
              Contact Support
            </Link>
          </div>
        </StateMessage>
      );
    }

    if (submitted) {
      return (
        <StateMessage
          tone="success"
          title="Return request submitted"
          text={`We've logged your request as ${submitted._id}. Keep that reference — you can follow the outcome on the return status page, and we'll email you as soon as it's reviewed.`}
        >
          <div className="c4rtn__actions">
            <Link
              className="c4rtn__btn c4rtn__btn--primary"
              to={process.env.PUBLIC_URL + "/return-status"}
            >
              Check Return Status
            </Link>
            <Link
              className="c4rtn__btn c4rtn__btn--outline"
              to={process.env.PUBLIC_URL + "/shop-grid-standard"}
            >
              Continue Shopping
            </Link>
          </div>
        </StateMessage>
      );
    }

    if (!order) {
      if (!searched) {
        return (
          <StateMessage
            title="Start with your order"
            text="Enter the order ID from your confirmation email and we'll pull up the items you can return."
          />
        );
      }
      return null;
    }

    return (
      <form className="c4rtn__results" onSubmit={handleRefundSubmit} noValidate>
        <SummaryBar
          eyebrow="Returning from order"
          reference={orderNumber}
          meta={[
            [order.billingInfo?.firstName, order.billingInfo?.lastName]
              .filter(Boolean)
              .join(" "),
            lines.length
              ? `${lines.length} ${lines.length === 1 ? "item" : "items"}`
              : null,
            order.overallTotal != null ? formatPrice(order.overallTotal) : null,
          ]}
          badge={ORDER_STATUS_LABELS[order.status] || order.status}
          tone={order.status === "Delivered" ? "done" : "review"}
        />

        <StepCard
          step={1}
          id="c4rtn-step-1"
          title="Select The Item To Return"
          subtitle="One item per request — send a second request if you need to return more."
        >
          <ItemPicker
            lines={lines}
            selected={selectedProduct}
            quantity={returnQty}
            onSelect={handleSelect}
            onQuantity={setReturnQty}
            formatPrice={formatPrice}
          />
        </StepCard>

        <StepCard
          step={2}
          id="c4rtn-step-2"
          title="Reason For Return"
          subtitle="Photos of the item and its packaging help us settle the claim faster."
        >
          <ChoiceGrid
            name="c4rtn-reason"
            options={RETURN_REASONS}
            value={reasonChoice}
            onChange={setReasonChoice}
            columns={3}
          />

          <div className="c4rtn__field c4rtn__field--full">
            <label className="c4rtn__label" htmlFor="c4rtn-comments">
              Additional comments
            </label>
            <textarea
              className="c4rtn__textarea"
              id="c4rtn-comments"
              rows="4"
              placeholder="Tell us what happened — the more detail, the quicker the review."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>

          <PhotoDropzone files={images} onChange={setImages} />
        </StepCard>

        <StepCard
          step={3}
          id="c4rtn-step-3"
          title="Preferred Resolution"
          subtitle="We'll aim for your preference, though the final outcome depends on the inspection."
        >
          <ChoiceGrid
            name="c4rtn-resolution"
            options={RESOLUTIONS}
            value={resolution}
            onChange={setResolution}
            columns={3}
          />
        </StepCard>

        <PolicyNote />

        {formError ? (
          <p className="c4rtn__form-error" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="c4rtn__footer-actions">
          <button
            className="c4rtn__btn c4rtn__btn--primary"
            type="submit"
            disabled={submitting}
          >
            {submitting ? "Submitting…" : "Submit Return Request"}
          </button>
          <Link
            className="c4rtn__btn c4rtn__btn--outline"
            to={process.env.PUBLIC_URL + "/my-account"}
          >
            Cancel
          </Link>
        </div>
      </form>
    );
  };

  return (
    <Fragment>
      <SEO
        titleTemplate="Return Request"
        description="Start a return or refund request for a Cey4Hub order."
      />

      <LayoutOne headerTop="visible">
        <main className="c4rtn">
          <div className="c4wide">
            <nav className="c4rtn__crumbs" aria-label="Breadcrumb">
              <Link to={process.env.PUBLIC_URL + "/"}>Home</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Return request</span>
            </nav>

            <LookupPanel
              id="c4rtn-lookup"
              title="Request A Return"
              subtitle="Find your order, choose the item and tell us what went wrong."
              label="Order ID"
              placeholder="e.g. 65f2c1a9b4d3e07a1c8f4b21"
              help="Your Order ID is on your receipt and in your order confirmation email. Returns open once an order has been confirmed."
              action="Find Order"
              loadingAction="Searching…"
              isLoading={loading}
              onSubmit={fetchOrder}
            />

            {results()}
          </div>
        </main>

        <NewsletterBar />
      </LayoutOne>
    </Fragment>
  );
}

export default Refund;
